import SocketCommand from '@/api/SocketCommand.js'
import {clone, sortBy} from '@/lib/helper/helper.js'

/**
 * Handles incoming order update socket commands.
 *
 * @param {string} command - The socket command name.
 * @param {Object} data - The payload containing action and id.
 */
export const handleKdsUpdate = (command, data) => {
  if (!data?.uid || !data?.action) return
  
  if (data.action === 'update' && userCanAccessOrder(data?.staff_id) && userCanAccessOrderWithSourceType(data?.source_type)) {
    getKdsOrder(data.uid, data.action)
  } else if (data.action === 'create' && userCanAccessOrder(data?.staff_id) && userCanAccessOrderWithSourceType(data?.source_type)) {
    getKdsOrder(data.uid, data.action)
  } else if (data.action === 'delete' && userCanAccessOrder(data?.staff_id)) {
    removeKdsOrder(data.uid)
  }
}

/**
 * Requests the details of a specific kds order via socket command.
 *
 * @param {number|string} uid - The UID of the order to retrieve.
 * @param {string} [action='update'] - The action (update or create) of the order to retrieve.
 */
export const getKdsOrder = (uid, action = 'update') => {
  if (!uid) return
  window.callAS(SocketCommand.Kds.Order.Get, {uid: uid, action: action || null})
}

/**
 * Determines whether to add or update an order in the kds list based on the action.
 *
 * @param {Array<Object>} orders - Array with orders object.
 * @param {string} action - The action to perform ('create' or 'update').
 */
export const addOrUpdateKdsOrder = (orders, action) => {
  if (!orders || !action) return

  if (action === 'update') updateKdsOrder(orders)
  if (action === 'create') addKdsOrder(orders)
}


/**
 * Updates an existing order in the kds list.
 *
 * @param {Array<Object>} orders - Array with orders object containing updated data.
 */
export const updateKdsOrder = (orders) => {
  if (!Array.isArray(orders) || !orders?.length) return

  const allOrders = getOrders()

  orders.forEach(order => {
    const index = getOrderIndex(allOrders, order.Id)
    if (index > -1) allOrders[index] = order
  })

  updateKdsOrderList(allOrders)
}

/**
 * Adds a new order to the kds list and sorts the list.
 *
 * @param {Array<Object>} orders - Array with orders object to be added.
 */
export const addKdsOrder = (orders) => {
  if (!Array.isArray(orders) || !orders?.length) return

  let allOrders = getOrders()

  orders.forEach(order => {
    const index = getOrderIndex(allOrders, order.Id)
    if (index < 0) allOrders.push(order)
  })

  allOrders = sortBy(allOrders, 'Timestamp', 'desc')
  updateKdsOrderList(allOrders)
}

/**
 * Removes orders from the kds order list by their IDs.
 *
 * @param {Array<number|string>} id - An array of order IDs to be removed.
 */
export const removeKdsOrder = (id) => {
  if (!Array.isArray(id) || !id?.length) return
  const orders = getOrders()

  id.forEach(orderId => {
    const index = getOrderIndex(orders, orderId)
    if (index > -1) orders.splice(index, 1)
  })

  updateKdsOrderList(orders)
}

/**
 * Updates the kds order list in the data store and emits a socket command event.
 *
 * @param {Array<Object>} orders - The updated list of order objects.
 */
const updateKdsOrderList = (orders) => {
  if (!Array.isArray(orders)) return

  const vm = window?.Vue
  vm.$set(vm.$DataStore.kds, 'orders', orders)
  vm.$bus.$emit(SocketCommand.Kds.All, orders)
}

/**
 * Retrieves the current list of kds orders from the data store.
 *
 * @returns {Array<Object>} A cloned array of order objects.
 */
const getOrders = () => {
  const vm = window?.Vue
  return clone(vm.$DataStore?.kds?.orders || [])
}

/**
 * Finds the index of an order within an array by its ID.
 *
 * @param {Array<Object>} orders - The array of order objects to search.
 * @param {number|string} orderId - The ID of the order to find.
 * @returns {number} The index of the order if found, otherwise -1.
 */
const getOrderIndex = (orders, orderId) => {
  return orders?.findIndex(order => order.Id === orderId) ?? -1
}

const userCanAccessOrderWithSourceType = (orderSourceTypeId) => {
  if (!orderSourceTypeId) return false

  const vm = window.Vue
  const userAllowedSourceTypeIds = vm.posUser?.SourceTypeIds
  return userAllowedSourceTypeIds ? userAllowedSourceTypeIds.includes(orderSourceTypeId) : true
}

const userCanAccessOrder = (orderStaffIds) => {
  const vm = window.Vue

  if (vm?.userIsOwner || vm?.userIsSuperAdmin) return true

  if (!Array.isArray(orderStaffIds) || !orderStaffIds?.length) return false

  return orderStaffIds.includes(parseInt(vm?.posUser?.staff_id))
}
